import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MusicOffIcon from "@mui/icons-material/MusicOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import NumbersIcon from "@mui/icons-material/Numbers";
import SettingsIcon from "@mui/icons-material/Settings";
import TollIcon from "@mui/icons-material/Toll";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Popover,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Stack,
    Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StoryReferenceWithGrid from "../../../admin/story/StoryReferenceWithGrid";
import IconBack from "../../components/IconBack";
import IconImagePreview from "../../components/IconImagePreview";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import { DIFF_LEVELS, templateForGridAndLevel } from "../../../admin/story/adminSlidingUi";
import { useConfetti } from "../../../context/ConfettiContext";
import AuthService from "../../../services/AuthService";
import HomeCreditService from "../../../services/HomeCreditService";
import Logger from "../../../services/Logger";
import UserService from "../../../services/UserService";
import { resolveWebHintPuzzleCoins } from "../../../services/PuzzleCoinsUtil";
import { usePagesTranslation } from "../../locales";
import AudioButton from "../../components/AudioButton";
import { PUZZLE_BGM_TRACKS } from "../../components/bgmTracks";
import LoginConfirmDialog from "../../components/LoginConfirmDialog";
import PlayLayoutSplitSelect from "../../components/PlayLayoutSplitSelect";
import PlayToolbarScrollRow from "../../components/PlayToolbarScrollRow";
import PlayElapsedTimer from "../common/components/PlayElapsedTimer";
// import GamePausedOverlay from "../common/components/GamePausedOverlay";
import usePlayVisibilityPause from "../common/hooks/usePlayVisibilityPause";
import SocialShareDropdown, { buildShareUrl } from "../common/components/SocialShareDropdown";
import PuzzleSolvedOriginalImage from "../common/components/PuzzleSolvedOriginalImage";
import SuccessCard from "../common/components/SuccessCard";
import FeedbackService from "../common/feedback/FeedbackService";
import SlidingPlayService from "./SlidingPlayService";
import SlidingPlayMistBackground from "./SlidingPlayMistBackground";
import {
    buildGhostGuideMoves,
    GHOST_HINT_ALL_MS,
    GHOST_HINT_GAP_MS,
    GHOST_HINT_MOVE_COUNT,
    GHOST_HINT_STEP_MS,
} from "./slidingSolveHint";
import {
    resolveDefaultSlidingSelection,
    resolveSlidingPlaySelection,
    saveSlidingPlayPrefs,
} from "../common/puzzlePlayPrefs";

/** Only these sizes appear in the public UI (must exist on a template). */
const SIZE_OPTIONS = [4, 6];

/** Reference : puzzle column ratios (sm+). xs = single column. Same as admin sliding detail. */
const SPLIT_PRESETS = [
    { key: "4-8", label: "1/3", sm: "minmax(0, 1fr) minmax(0, 3fr)" },
    { key: "6-6", label: "3/3", sm: "minmax(0, 6fr) minmax(0, 6fr)" },
    { key: "hide", label: "100%", sm: "1fr" },
];

/** Story picker (size/level selectors): balanced split. Board play (`hideSelectors`): puzzle only. */
const DEFAULT_SPLIT_KEY = "6-6";
const BOARD_PLAY_DEFAULT_SPLIT_KEY = "hide";
const SLIDING_LAYOUT_SPLIT_KEY = "sliding.layoutSplit";
const REFERENCE_ON_SCREEN_KEY = "sliding.referenceOnScreen";
const TILE_NUMBER_HINT_KEY = "sliding.tileNumberHintSeen";
const TILE_NUMBER_THEME_KEY = "sliding.tileNumberTheme";

/** Same icon sizing as jigsaw play toolbar. */
const PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    padding: "5px",
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
};

/** Toolbar text/status sits straight on the night mist backdrop. */
const MIST_TOOLBAR_FG = "#eaf6ef";

/** Pause on the full solved image before the success overlay. */
const SOLVED_IMAGE_REVEAL_MS = 3000;

function loadBoardSplitKey() {
    try {
        const v = localStorage.getItem(SLIDING_LAYOUT_SPLIT_KEY);
        if (v === "4-8" || v === "6-6" || v === "hide") return v;
    } catch {}
    return BOARD_PLAY_DEFAULT_SPLIT_KEY;
}

function loadReferenceOnScreen() {
    try {
        const v = localStorage.getItem(REFERENCE_ON_SCREEN_KEY);
        if (v === "0" || v === "false") return false;
        if (v === "1" || v === "true") return true;
        const legacy = localStorage.getItem(SLIDING_LAYOUT_SPLIT_KEY);
        if (legacy === "hide") return false;
        if (legacy === "4-8" || legacy === "6-6") return true;
    } catch {}
    return false;
}

function persistReferenceOnScreen(onScreen) {
    try {
        localStorage.setItem(REFERENCE_ON_SCREEN_KEY, onScreen ? "1" : "0");
    } catch {
        /* ignore */
    }
}

function loadTileNumberTheme() {
    try {
        const v = localStorage.getItem(TILE_NUMBER_THEME_KEY);
        if (v === "dark" || v === "light") return v;
    } catch {
        /* ignore */
    }
    return "light";
}

function persistTileNumberTheme(theme) {
    try {
        localStorage.setItem(TILE_NUMBER_THEME_KEY, theme);
    } catch {
        /* ignore */
    }
}

/** Largest 1:1 square that fits a sized flex region (portrait or landscape). */
const boardSquareSlotSx = {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    containerType: "size",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const boardSquareSx = {
    aspectRatio: "1 / 1",
    width: "min(100cqw, 100cqh)",
    height: "min(100cqw, 100cqh)",
    maxWidth: "100%",
    maxHeight: "100%",
    flexShrink: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    "@supports not (width: 1cqw)": {
        width: "100%",
        height: "auto",
    },
};

const boardSquareInnerSx = {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
};

/** Maps layout preset to MUI Grid column spans (12 cols on sm+); xs stacks full width. */
function splitKeyToGridSpans(splitKey) {
    if (splitKey === "hide") return { left: 0, right: 12 };
    if (splitKey === "4-8") return { left: 3, right: 9 };
    if (splitKey === "12-12") return { left: 12, right: 12 };
    return { left: 6, right: 6 };
}

/**
 * Selected ToggleButton styles — same as `/account/settings` ({@link SettingsMain} content/UI language & PDF format groups).
 */
const ACCOUNT_SETTINGS_TOGGLE_GROUP_SX = {
    "& .MuiToggleButton-root.Mui-selected": {
        borderColor: "#3BA666 !important",
        borderWidth: "2px !important",
        borderStyle: "solid !important",
        borderLeftColor: "#3BA666 !important",
        borderRightColor: "#3BA666 !important",
        borderTopColor: "#3BA666 !important",
        borderBottomColor: "#3BA666 !important",
        backgroundColor: "rgba(59, 166, 102, 0.08)",
        "&:hover": {
            backgroundColor: "rgba(59, 166, 102, 0.12)",
        },
    },
};

function uniqueSizesPresent(templates) {
    const set = new Set();
    if (!Array.isArray(templates)) return [];
    for (const t of templates) {
        const g = Number(t?.gridSize);
        if (Number.isFinite(g) && g > 0) set.add(g);
    }
    return SIZE_OPTIONS.filter((s) => set.has(s));
}

const LEVEL_LABEL_KEYS = {
    EASY: "puzzles-browse.sliding-play-level-easy",
    MEDIUM: "puzzles-browse.sliding-play-level-medium",
    HARD: "puzzles-browse.sliding-play-level-hard",
};

function normalizeLayoutArray(raw, n) {
    if (!Array.isArray(raw) || raw.length !== n || n <= 0) return null;
    return raw.map((x) => Number(x));
}

function isSolvedPermutation(layout) {
    return Array.isArray(layout) && layout.length > 0 && layout.every((v, i) => Number(v) === i);
}

function apiPayloadOk(ro) {
    if (!ro || typeof ro !== "object") return false;
    if (ro.code === "FAILURE") return false;
    if (ro.error != null && ro.error !== "") return false;
    return true;
}

/** Same duration formatting as grid play SuccessCard. */
function formatSolveDurationMs(ms) {
    if (ms == null || Number.isNaN(ms) || ms < 0) ms = 0;
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Public sliding play: reference image (grid) left, puzzle right — layout presets and size/level like admin.
 * No story body text.
 */
export default function SlidingPuzzlePlay({
    templates,
    imageUrl,
    previewImageUrl,
    toolbarLeading,
    cloudUserId,
    isAuthenticated = Boolean(cloudUserId),
    storyId,
    hideSelectors = false,
    sharePuzzleTitle = "",
    soundEnabled = false,
    bgmAutoPlay = false,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const { triggerConfetti } = useConfetti();

    const [bgmPlaying, setBgmPlaying] = useState(bgmAutoPlay);
    useEffect(() => {
        setBgmPlaying(bgmAutoPlay);
    }, [bgmAutoPlay]);

    const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(soundEnabled !== false);
    useEffect(() => {
        setSoundEffectsEnabled(soundEnabled !== false);
    }, [soundEnabled]);

    const playMoveSound = soundEffectsEnabled;

    const [successCardOpen, setSuccessCardOpen] = useState(false);
    const [successCardStats, setSuccessCardStats] = useState({ time: "0:00", moves: 0 });
    const [successSpeedRank, setSuccessSpeedRank] = useState(null);
    const [successRating, setSuccessRating] = useState(0);
    const [successRatingBusy, setSuccessRatingBusy] = useState(false);
    const [puzzleSolved, setPuzzleSolved] = useState(false);

    const sizesAvailable = useMemo(() => uniqueSizesPresent(templates), [templates]);

    const [gridSize, setGridSize] = useState(4);
    const [level, setLevel] = useState("EASY");
    const [splitKey, setSplitKeyState] = useState(
        hideSelectors ? loadBoardSplitKey() : DEFAULT_SPLIT_KEY,
    );
    const [referenceOnScreen, setReferenceOnScreen] = useState(() =>
        hideSelectors ? loadReferenceOnScreen() : false,
    );
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [showTileNumbers, setShowTileNumbers] = useState(false);
    const [tileNumberTheme, setTileNumberTheme] = useState(loadTileNumberTheme);
    const showTileNumbersRef = useRef(false);
    showTileNumbersRef.current = showTileNumbers;

    const handleTileNumberThemeChange = useCallback((event, next) => {
        if (next == null) return;
        setTileNumberTheme(next);
        persistTileNumberTheme(next);
    }, []);
    const [tileNumberHintOpen, setTileNumberHintOpen] = useState(() => {
        try {
            return localStorage.getItem(TILE_NUMBER_HINT_KEY) !== "1";
        } catch {
            return false;
        }
    });
    const [tileNumberBtnEl, setTileNumberBtnEl] = useState(null);
    const dismissTileNumberHint = useCallback(() => {
        setTileNumberHintOpen(false);
        try {
            localStorage.setItem(TILE_NUMBER_HINT_KEY, "1");
        } catch {}
    }, []);
    useEffect(() => {
        if (!tileNumberHintOpen) return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (tileNumberBtnEl && tileNumberBtnEl.contains(target)) return;
            if (target.closest(".MuiPopover-root")) return;
            dismissTileNumberHint();
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [tileNumberHintOpen, tileNumberBtnEl, dismissTileNumberHint]);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [timerPaused, setTimerPaused] = useState(false);
    const { visibilityPaused, continuePlaying } = usePlayVisibilityPause({
        enabled: hideSelectors && !puzzleSolved,
    });
    const timerEffectivelyPaused = timerPaused || visibilityPaused;
    const [ghostGuide, setGhostGuide] = useState(null);
    const [ghostHintBusy, setGhostHintBusy] = useState(false);
    const [ghostHintMessage, setGhostHintMessage] = useState("");
    const [ghostLoginDialogOpen, setGhostLoginDialogOpen] = useState(false);
    const [walletPlayCredits, setWalletPlayCredits] = useState(null);
    const [walletHintCredits, setWalletHintCredits] = useState(null);
    const [walletPuzzleCoins, setWalletPuzzleCoins] = useState(null);
    const [walletBalancesLoading, setWalletBalancesLoading] = useState(false);
    const bgmButtonWrapRef = useRef(null);
    const timerPauseStartedAtRef = useRef(null);
    const ghostHintRunIdRef = useRef(0);
    const liveLayoutRef = useRef(null);
    const setSplitKey = useCallback(
        (next) => {
            if (next !== "4-8" && next !== "6-6" && next !== "hide") return;
            setSplitKeyState(next);
            if (hideSelectors) {
                try {
                    localStorage.setItem(SLIDING_LAYOUT_SPLIT_KEY, next);
                } catch {}
            }
        },
        [hideSelectors],
    );
    const templatesInitKey = useMemo(
        () => (templates ?? []).map((t) => `${t?.id ?? ""}:${t?.gridSize ?? ""}:${t?.level ?? ""}`).join("|"),
        [templates]
    );
    useEffect(() => {
        if (!templates?.length) return;
        if (templates.length === 1 && hideSelectors) {
            const single = templates[0];
            const gs = Number(single?.gridSize);
            const lv = String(single?.difficultyLevel ?? "").toUpperCase();
            if (Number.isFinite(gs) && gs > 0 && DIFF_LEVELS.includes(lv)) {
                setGridSize(gs);
                setLevel(lv);
                return;
            }
        }
        const init = cloudUserId
            ? resolveSlidingPlaySelection(templates, cloudUserId)
            : resolveDefaultSlidingSelection(templates);
        setGridSize(init.gridSize);
        setLevel(init.level);
    }, [templatesInitKey, cloudUserId, hideSelectors]);

    useEffect(() => {
        if (!cloudUserId) return;
        saveSlidingPlayPrefs(cloudUserId, { gridSize, level });
    }, [cloudUserId, gridSize, level]);

    const tpl = useMemo(
        () => templateForGridAndLevel(templates, gridSize, level),
        [templates, gridSize, level]
    );

    const resolvedGs =
        tpl?.gridSize != null && Number(tpl.gridSize) > 0 ? Number(tpl.gridSize) : Number(gridSize);

    const layout = tpl?.startingLayout;
    const n = Number.isFinite(resolvedGs) && resolvedGs > 0 ? resolvedGs * resolvedGs : 0;
    const canRenderPuzzle =
        tpl &&
        imageUrl &&
        Number.isFinite(resolvedGs) &&
        resolvedGs >= 1 &&
        Array.isArray(layout) &&
        layout.length === n;

    const slidingTemplateId =
        tpl?.id != null && String(tpl.id).trim() !== "" ? String(tpl.id).trim() : null;
    const templatePreviewImageUrl = (() => {
        const u = tpl?.previewImageUrl ?? tpl?.preview_image_url;
        return typeof u === "string" && u.trim() ? u.trim() : "";
    })();
    const slidingShareUrl = slidingTemplateId ? buildShareUrl(slidingTemplateId, "SLIDING") : "";
    const canShareSliding = Boolean(slidingShareUrl && templatePreviewImageUrl);

    const syncPlay = Boolean(cloudUserId && slidingTemplateId && canRenderPuzzle);

    const [sessionLayoutOverride, setSessionLayoutOverride] = useState(null);
    /** When syncPlay is true, false until list/start finishes (handles late-loading cloudUserId). */
    const [sessionFetchDone, setSessionFetchDone] = useState(false);

    const progressDebounceRef = useRef(null);
    const completionSentRef = useRef(false);
    const playSessionStartMsRef = useRef(null);
    const successCardShownRef = useRef(false);
    const successRevealTimerRef = useRef(null);
    const sessionStartsSolvedCapturedRef = useRef(false);
    const moveCountRef = useRef(0);
    const [moveCount, setMoveCount] = useState(0);
    /** True only when the board was already solved on first load — not after the user solves mid-session. */
    const [sessionStartsSolved, setSessionStartsSolved] = useState(false);

    const puzzleBoardReady = !syncPlay || sessionFetchDone;

    useEffect(() => {
        completionSentRef.current = false;
        setSessionLayoutOverride(null);
        successCardShownRef.current = false;
        sessionStartsSolvedCapturedRef.current = false;
        setSessionStartsSolved(false);
        if (successRevealTimerRef.current) {
            clearTimeout(successRevealTimerRef.current);
            successRevealTimerRef.current = null;
        }
        moveCountRef.current = 0;
        setMoveCount(0);
        playSessionStartMsRef.current = null;
        setPuzzleSolved(false);
        setSuccessCardOpen(false);
        setSuccessSpeedRank(null);
    }, [slidingTemplateId]);

    useEffect(() => () => {
        if (successRevealTimerRef.current) {
            clearTimeout(successRevealTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (!syncPlay) {
            setSessionFetchDone(false);
            return;
        }

        let cancelled = false;
        setSessionFetchDone(false);
        setSessionLayoutOverride(null);

        (async () => {
            try {
                const page = await SlidingPlayService.listSlidingPlay({
                    page: 0,
                    pageSize: 1,
                    sort: "updatedAt:desc",
                    slidingId: slidingTemplateId,
                    isCompleted: false,
                });
                const row = page.content?.[0];
                const fromList = normalizeLayoutArray(
                    row?.currentLayout ?? row?.current_layout,
                    n
                );
                if (!cancelled && fromList) {
                    setSessionLayoutOverride(fromList);
                } else {
                    const started = await SlidingPlayService.startSlidingPlay(cloudUserId, slidingTemplateId);
                    if (!cancelled && apiPayloadOk(started)) {
                        const fromStart = normalizeLayoutArray(
                            started?.currentLayout ?? started?.current_layout,
                            n
                        );
                        if (fromStart) {
                            setSessionLayoutOverride(fromStart);
                        }
                    }
                }
            } catch (e) {
                Logger.error("Sliding puzzle session sync:", e);
            }
            if (!cancelled) setSessionFetchDone(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [syncPlay, cloudUserId, slidingTemplateId, n]);

    useEffect(() => {
        return () => {
            if (progressDebounceRef.current) {
                clearTimeout(progressDebounceRef.current);
            }
        };
    }, []);

    const effectiveStartingLayout = useMemo(() => {
        if (sessionLayoutOverride && sessionLayoutOverride.length === n) {
            return sessionLayoutOverride;
        }
        return layout;
    }, [sessionLayoutOverride, layout, n]);

    useEffect(() => {
        if (!canRenderPuzzle || !puzzleBoardReady) return;
        if (playSessionStartMsRef.current == null) {
            playSessionStartMsRef.current = Date.now();
        }
    }, [canRenderPuzzle, puzzleBoardReady]);

    useEffect(() => {
        if (!canRenderPuzzle || !puzzleBoardReady || !effectiveStartingLayout?.length || !n) return;
        if (syncPlay && !sessionFetchDone) return;
        if (sessionStartsSolvedCapturedRef.current) return;
        sessionStartsSolvedCapturedRef.current = true;
        setSessionStartsSolved(isSolvedPermutation(effectiveStartingLayout));
    }, [canRenderPuzzle, puzzleBoardReady, effectiveStartingLayout, n, syncPlay, sessionFetchDone]);

    useEffect(() => {
        if (!puzzleBoardReady || !syncPlay || !effectiveStartingLayout?.length) return;
        if (isSolvedPermutation(effectiveStartingLayout)) {
            completionSentRef.current = true;
            if (sessionStartsSolved) {
                setPuzzleSolved(true);
            }
        }
    }, [puzzleBoardReady, syncPlay, effectiveStartingLayout, sessionStartsSolved]);

    const handleSlidingMove = useCallback(() => {
        moveCountRef.current += 1;
        setMoveCount(moveCountRef.current);
    }, []);

    const handleBoardLayoutChange = useCallback(
        (nextLayout) => {
            if (!puzzleBoardReady || !canRenderPuzzle) return;
            const normalized = normalizeLayoutArray(nextLayout, n);
            if (!normalized) return;

            liveLayoutRef.current = normalized;
            setSessionLayoutOverride(normalized);

            if (isSolvedPermutation(normalized)) {
                if (progressDebounceRef.current) {
                    clearTimeout(progressDebounceRef.current);
                    progressDebounceRef.current = null;
                }

                if (!successCardShownRef.current && !sessionStartsSolved) {
                    successCardShownRef.current = true;
                    if (timerPauseStartedAtRef.current != null && playSessionStartMsRef.current != null) {
                        playSessionStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
                        timerPauseStartedAtRef.current = null;
                    }
                    setTimerPaused(false);
                    const startedAt = playSessionStartMsRef.current ?? Date.now();
                    const elapsedMs = Date.now() - startedAt;
                    setSuccessCardStats({
                        time: formatSolveDurationMs(elapsedMs),
                        moves: moveCountRef.current,
                    });
                    triggerConfetti(10000);
                    if (hideSelectors) {
                        // Full image + success dialog after tile scatter (`onSolvedRevealComplete`).
                    } else {
                        setPuzzleSolved(true);
                        if (successRevealTimerRef.current) {
                            clearTimeout(successRevealTimerRef.current);
                        }
                        successRevealTimerRef.current = setTimeout(() => {
                            successRevealTimerRef.current = null;
                            setSuccessRating(0);
                            setSuccessCardOpen(true);
                        }, SOLVED_IMAGE_REVEAL_MS);
                    }
                }

                if (syncPlay && slidingTemplateId && cloudUserId) {
                    if (!completionSentRef.current) {
                        completionSentRef.current = true;
                        void SlidingPlayService.progressSlidingPlay(cloudUserId, slidingTemplateId, {
                            currentLayout: normalized,
                            isCompleted: true,
                            tileMode: showTileNumbersRef.current ? "numbers" : "image",
                        }).then((data) => {
                            if (data?.speedRank) {
                                setSuccessSpeedRank(data.speedRank);
                            }
                        }).catch((err) => {
                            Logger.error("Sliding completion progress failed:", err);
                        });
                    }
                }
                return;
            }

            completionSentRef.current = false;
            if (!syncPlay || !slidingTemplateId || !cloudUserId) return;

            if (progressDebounceRef.current) clearTimeout(progressDebounceRef.current);
            progressDebounceRef.current = setTimeout(() => {
                progressDebounceRef.current = null;
                void SlidingPlayService.progressSlidingPlay(cloudUserId, slidingTemplateId, {
                    currentLayout: normalized,
                    isCompleted: false,
                });
            }, 450);
        },
        [
            puzzleBoardReady,
            canRenderPuzzle,
            n,
            sessionStartsSolved,
            syncPlay,
            slidingTemplateId,
            cloudUserId,
            triggerConfetti,
            hideSelectors,
        ]
    );

    const handleSolveRevealComplete = useCallback(() => {
        setPuzzleSolved(true);
        if (successRevealTimerRef.current) {
            clearTimeout(successRevealTimerRef.current);
        }
        successRevealTimerRef.current = setTimeout(() => {
            successRevealTimerRef.current = null;
            setSuccessRating(0);
            setSuccessCardOpen(true);
        }, SOLVED_IMAGE_REVEAL_MS);
    }, []);

    const splitSpans = splitKeyToGridSpans(splitKey);

    const referenceDisplayUrl = useMemo(
        () => (imageUrl || previewImageUrl || "").trim() || null,
        [imageUrl, previewImageUrl],
    );

    const toggleReferenceOnScreen = useCallback(() => {
        setReferenceOnScreen((prev) => {
            const next = !prev;
            persistReferenceOnScreen(next);
            return next;
        });
    }, []);

    const hideReferenceOnScreen = useCallback(() => {
        setReferenceOnScreen(false);
        persistReferenceOnScreen(false);
    }, []);

    const handleOpenFullImage = useCallback(() => {
        setSettingsAnchor(null);
        setImageDialogOpen(true);
    }, []);

    const handleToggleReferenceFromMenu = useCallback(() => {
        setSettingsAnchor(null);
        toggleReferenceOnScreen();
    }, [toggleReferenceOnScreen]);

    const handleToggleTimerPause = useCallback(() => {
        setTimerPaused((prev) => {
            const next = !prev;
            if (next) {
                timerPauseStartedAtRef.current = Date.now();
            } else if (timerPauseStartedAtRef.current != null && playSessionStartMsRef.current != null) {
                playSessionStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
                timerPauseStartedAtRef.current = null;
            } else {
                timerPauseStartedAtRef.current = null;
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!hideSelectors || puzzleSolved) return undefined;
        if (visibilityPaused) {
            if (!timerPaused && timerPauseStartedAtRef.current == null) {
                timerPauseStartedAtRef.current = Date.now();
            }
            return undefined;
        }
        if (!timerPaused && timerPauseStartedAtRef.current != null && playSessionStartMsRef.current != null) {
            playSessionStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
            timerPauseStartedAtRef.current = null;
        }
        return undefined;
    }, [visibilityPaused, timerPaused, hideSelectors, puzzleSolved]);

    const handleToggleSoundEffects = useCallback(() => {
        setSoundEffectsEnabled((prev) => {
            const next = !prev;
            void (async () => {
                try {
                    const info = await UserService.getUserInfo();
                    const id = cloudUserId || info?.cloudUser?.id;
                    if (!id) return;
                    await UserService.updateUserProfile(id, {
                        ...UserService.getSettingsFormData(info),
                        soundEnabled: next,
                    });
                } catch (e) {
                    Logger.warn("SlidingPuzzlePlay save soundEnabled:", e);
                }
            })();
            return next;
        });
    }, [cloudUserId]);

    const loadWalletBalances = useCallback(async () => {
        if (!isAuthenticated) {
            setWalletPlayCredits(null);
            setWalletHintCredits(null);
            setWalletPuzzleCoins(null);
            return;
        }
        setWalletBalancesLoading(true);
        try {
            const [play, hints, userInfo] = await Promise.all([
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_CODE).catch(() => null),
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_HINTS_CODE).catch(() => null),
                UserService.getUserInfo().catch(() => null),
            ]);
            setWalletPlayCredits(play?.creditBalance ?? 0);
            setWalletHintCredits(hints?.creditBalance ?? 0);
            setWalletPuzzleCoins(resolveWebHintPuzzleCoins(userInfo?.cloudUser));
        } catch (err) {
            Logger.warn("SlidingPuzzlePlay load wallet balances:", err);
            setWalletPlayCredits(0);
            setWalletHintCredits(0);
            setWalletPuzzleCoins(0);
        } finally {
            setWalletBalancesLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!settingsAnchor || !isAuthenticated) return;
        void loadWalletBalances();
    }, [settingsAnchor, isAuthenticated, loadWalletBalances]);

    const handleGhostHintClick = useCallback(async () => {
        if (!hideSelectors || puzzleSolved || ghostHintBusy) return;
        if (!isAuthenticated) {
            setGhostHintMessage("");
            setGhostLoginDialogOpen(true);
            return;
        }
        const layoutNow = liveLayoutRef.current || effectiveStartingLayout;
        if (!Array.isArray(layoutNow) || layoutNow.length !== n || isSolvedPermutation(layoutNow)) {
            return;
        }

        const runId = ghostHintRunIdRef.current + 1;
        ghostHintRunIdRef.current = runId;
        setGhostHintBusy(true);
        setGhostHintMessage("");
        setGhostGuide(null);

        let moves = [];
        try {
            moves = await buildGhostGuideMoves(
                layoutNow,
                resolvedGs,
                null,
                GHOST_HINT_MOVE_COUNT,
            );
        } catch (err) {
            Logger.warn("Sliding ghost hint solve failed:", err);
        }

        if (ghostHintRunIdRef.current !== runId) return;

        if (!moves.length) {
            setGhostHintBusy(false);
            setGhostHintMessage(
                t("puzzle.sliding.ghost-hint-failed", "Could not find a hint for this board."),
            );
            return;
        }

        try {
            const spend = await HomeCreditService.consumeSlidingGhostHint();
            if (typeof spend?.creditBalance === "number") {
                setWalletHintCredits(spend.creditBalance);
            } else {
                void loadWalletBalances();
            }
        } catch (err) {
            const message = err?.message || "";
            Logger.warn("Sliding hint credit spend failed:", message);
            setGhostHintBusy(false);
            if (/insufficient/i.test(message)) {
                setGhostHintMessage(
                    t(
                        "puzzle.sliding.ghost-hint-insufficient",
                        "Not enough hint credits.",
                    ),
                );
            } else {
                setGhostHintMessage(
                    t("puzzle.sliding.ghost-hint-failed", "Could not start hint. Please try again."),
                );
            }
            return;
        }

        if (ghostHintRunIdRef.current !== runId) return;

        const wait = (ms) =>
            new Promise((resolve) => {
                window.setTimeout(resolve, ms);
            });

        try {
            for (let i = 0; i < moves.length; i += 1) {
                if (ghostHintRunIdRef.current !== runId) return;
                setGhostGuide({ mode: "step", moves, stepIndex: i });
                await wait(GHOST_HINT_STEP_MS);
                if (ghostHintRunIdRef.current !== runId) return;
                setGhostGuide(null);
                await wait(GHOST_HINT_GAP_MS);
            }
            if (ghostHintRunIdRef.current !== runId) return;
            setGhostGuide({ mode: "all", moves, stepIndex: null });
            await wait(GHOST_HINT_ALL_MS);
            if (ghostHintRunIdRef.current !== runId) return;
            setGhostGuide(null);
        } finally {
            if (ghostHintRunIdRef.current === runId) {
                setGhostHintBusy(false);
            }
        }
    }, [
        hideSelectors,
        puzzleSolved,
        ghostHintBusy,
        isAuthenticated,
        effectiveStartingLayout,
        n,
        resolvedGs,
        t,
        loadWalletBalances,
    ]);

    useEffect(() => () => {
        ghostHintRunIdRef.current += 1;
    }, []);

    const showReferencePanel =
        hideSelectors && referenceOnScreen && Boolean(referenceDisplayUrl) && !puzzleSolved;

    /** Board play: puzzle stays centered; preview sits in the left leftover space. */
    const useBoardAdaptiveSplit = showReferencePanel;
    const boardPuzzleOnlyLayout = hideSelectors && !showReferencePanel && !puzzleSolved;

    const successCardNode = (
        <SuccessCard
            variant="modal"
            open={successCardOpen}
            onClose={() => setSuccessCardOpen(false)}
            stats={successCardStats}
            speedRank={successSpeedRank}
            showSpeedInvite
            guest={!isAuthenticated}
            celebrationSoundEnabled={playMoveSound}
            showRating={isAuthenticated}
            ratingValue={successRating}
            ratingBusy={successRatingBusy}
            onRate={async (stars) => {
                setSuccessRating(stars);
                setSuccessRatingBusy(true);
                try {
                    await FeedbackService.submitFeedback({
                        cloudUserId,
                        stars,
                        details: {
                            puzzleType: "SLIDING",
                            slidingId: slidingTemplateId,
                        },
                    });
                } catch (e) {
                    Logger.error("Sliding rating failed:", e);
                } finally {
                    setSuccessRatingBusy(false);
                }
            }}
            onNext={() => {
                setSuccessCardOpen(false);
                if (!isAuthenticated) {
                    void AuthService.signup().catch((e) => Logger.error("Signup redirect:", e));
                } else {
                    navigate("/puzzles/sliding");
                }
            }}
        />
    );

    const puzzleBoardBox = (fullscreen = false, fillParent = false) => (
        <Box
            sx={{
                width: "100%",
                height: fullscreen || fillParent ? "100%" : "auto",
                flex: fullscreen || fillParent ? "1 1 auto" : "1 1 auto",
                minHeight: fullscreen || fillParent ? 0 : undefined,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: fullscreen || fillParent ? "none" : "4px solid",
                borderColor: "#7FB85A",
                borderRadius: fullscreen || fillParent ? 0 : 1,
                boxSizing: "border-box",
                ...(hideSelectors
                    ? {
                          position: "relative",
                          "@keyframes slidingBoardAura": {
                              "0%, 100%": {
                                  boxShadow:
                                      "0 0 0 0 rgba(127,184,90,0), 0 12px 40px rgba(40,80,60,0.18)",
                              },
                              "50%": {
                                  boxShadow:
                                      "0 0 28px 4px rgba(127,184,90,0.35), 0 16px 48px rgba(40,80,60,0.22)",
                              },
                          },
                          animation: "slidingBoardAura 5.5s ease-in-out infinite",
                          borderRadius: 2,
                          overflow: "visible",
                      }
                    : {}),
            }}
        >
            {syncPlay && !puzzleBoardReady ? (
                <Box
                    sx={{
                        width: "100%",
                        height: fullscreen || fillParent ? "100%" : "auto",
                        alignSelf: "stretch",
                        minHeight: fullscreen || fillParent ? 0 : { xs: 200, sm: 260 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: fullscreen ? 0 : 4,
                        boxSizing: "border-box",
                    }}
                >
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <SlidingPlayMain
                    imageUrl={imageUrl}
                    gridSize={resolvedGs}
                    startingLayout={effectiveStartingLayout}
                    maxBoardWidth={null}
                    fillParent={fillParent}
                    hideCompletedCaption
                    animateSolveReveal={hideSelectors && !sessionStartsSolved}
                    onSolvedRevealComplete={hideSelectors ? handleSolveRevealComplete : undefined}
                    playMoveSound={playMoveSound}
                    ghostGuide={ghostGuide}
                    onLayoutChange={handleBoardLayoutChange}
                    onMove={handleSlidingMove}
                    showTileNumbers={showTileNumbers}
                    tileNumberTheme={tileNumberTheme}
                    slideTransitionMs={180}
                />
            )}
        </Box>
    );

    const boardSplitOuterSx = {
        width: "100%",
        flex: "1 1 0",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
    };

    const boardPuzzleSlotSx = boardSquareSlotSx;

    const boardReferencePreview = referenceDisplayUrl ? (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "rgba(15, 23, 42, 0.35)",
            }}
        >
            <IconButton
                size="small"
                onClick={handleOpenFullImage}
                aria-label={t("puzzle.jigsaw.open-full-image", "Open full image")}
                sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    zIndex: 2,
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "common.white",
                    p: 0.25,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                }}
            >
                <OpenInNewIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            <IconButton
                size="small"
                onClick={hideReferenceOnScreen}
                aria-label={t("puzzle.jigsaw.hide-on-screen")}
                sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 2,
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "common.white",
                    p: 0.25,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                }}
            >
                <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            <Box
                component="img"
                src={referenceDisplayUrl}
                alt={t("puzzles-browse.sliding-play-reference-alt")}
                referrerPolicy="no-referrer"
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "contain",
                }}
            />
        </Box>
    ) : null;

    const solvedOriginalAlt = t("puzzles-browse.sliding-play-reference-alt");

    const renderSolvedOriginalSquare = () => (
        <Box sx={boardPuzzleSlotSx}>
            <Box
                sx={{
                    ...boardSquareSx,
                    border: "4px solid",
                    borderColor: "#7FB85A",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.900",
                }}
            >
                <Box sx={boardSquareInnerSx}>
                    <PuzzleSolvedOriginalImage imageUrl={referenceDisplayUrl} alt={solvedOriginalAlt} />
                </Box>
            </Box>
        </Box>
    );

    const boardPuzzleSquare = (
        <Box sx={boardPuzzleSlotSx}>
            <Box
                sx={{
                    ...boardSquareSx,
                    border: "4px solid",
                    borderColor: "#7FB85A",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box sx={boardSquareInnerSx}>
                    {puzzleSolved && referenceDisplayUrl
                        ? (
                            <PuzzleSolvedOriginalImage
                                imageUrl={referenceDisplayUrl}
                                alt={solvedOriginalAlt}
                            />
                        )
                        : puzzleBoardBox(false, true)}
                </Box>
            </Box>
        </Box>
    );

    const originalImageDialogUrl = referenceDisplayUrl;

    return (
        <Stack
            spacing={2}
            sx={{
                position: "relative",
                isolation: "isolate",
                width: "100%",
                ...(hideSelectors
                    ? {
                          flex: 1,
                          minHeight: 0,
                      }
                    : {
                          minHeight: { xs: "70vh", sm: "75vh" },
                      }),
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flex: hideSelectors ? 1 : undefined,
                    minHeight: hideSelectors ? 0 : undefined,
                    width: "100%",
                }}
            >
            {hideSelectors ? (
            <>
            <PlayToolbarScrollRow>
                {toolbarLeading ? (
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{toolbarLeading}</Box>
                ) : null}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    {canShareSliding ? (
                        <SocialShareDropdown
                            compact
                            puzzleTitle={
                                sharePuzzleTitle || t("puzzles-browse.sliding-play-puzzle-heading")
                            }
                            puzzleId={slidingTemplateId}
                            puzzleType="SLIDING"
                            shareUrl={slidingShareUrl}
                        />
                    ) : null}
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
                        <PlayElapsedTimer
                            active={hideSelectors}
                            running={!puzzleSolved && !timerEffectivelyPaused}
                            ariaLabel={t("puzzle.play-elapsed-time", "Elapsed time")}
                            textSx={{ color: MIST_TOOLBAR_FG }}
                        />
                        {!puzzleSolved ? (
                            <Tooltip
                                title={
                                    timerEffectivelyPaused
                                        ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                        : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                }
                                arrow
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        if (visibilityPaused) {
                                            continuePlaying();
                                            setTimerPaused((prev) => {
                                                if (
                                                    prev
                                                    && timerPauseStartedAtRef.current != null
                                                    && playSessionStartMsRef.current != null
                                                ) {
                                                    playSessionStartMsRef.current +=
                                                        Date.now() - timerPauseStartedAtRef.current;
                                                    timerPauseStartedAtRef.current = null;
                                                }
                                                return false;
                                            });
                                            return;
                                        }
                                        handleToggleTimerPause();
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        color: "#3BA666",
                                        ml: -0.75,
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                    }}
                                    aria-label={
                                        timerEffectivelyPaused
                                            ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                            : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                    }
                                    aria-pressed={timerEffectivelyPaused}
                                >
                                    {timerEffectivelyPaused ? (
                                        <PlayArrowIcon fontSize="small" />
                                    ) : (
                                        <PauseIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Box>
                    <Chip
                        size="small"
                        aria-label={t("puzzle.play-move-count", {
                            count: moveCount,
                            defaultValue: `Moves: ${moveCount}`,
                        })}
                        label={
                            <>
                                {t("success-card.stat-moves", "Moves")}{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {moveCount}
                                </Box>
                            </>
                        }
                        sx={{
                            flexShrink: 0,
                            height: "1.75rem",
                            "& .MuiChip-label": {
                                px: 1,
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                            },
                        }}
                    />
                    <Chip
                        size="small"
                        label={
                            showTileNumbers
                                ? t("puzzle.sliding.mode-numbers", "Numbers")
                                : t("puzzle.sliding.mode-image", "Image")
                        }
                        sx={{
                            flexShrink: 0,
                            height: "1.75rem",
                            "& .MuiChip-label": {
                                px: 1,
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                            },
                        }}
                    />
                </Box>
                <Tooltip
                    title={t("puzzle.sliding.ghost-hint", "Hint (1 credit)")}
                    arrow
                >
                    <span>
                        <IconButton
                            size="small"
                            data-play-toolbar-no-drag
                            disabled={puzzleSolved || ghostHintBusy}
                            onClick={() => {
                                void handleGhostHintClick();
                            }}
                            sx={{ flexShrink: 0, color: "#3BA666", ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX }}
                            aria-label={t("puzzle.sliding.ghost-hint", "Hint (1 credit)")}
                            aria-busy={ghostHintBusy}
                        >
                            {ghostHintBusy ? (
                                <CircularProgress size={16} thickness={5} sx={{ color: "#3BA666" }} />
                            ) : (
                                <LightbulbOutlinedIcon fontSize="small" />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
                {referenceDisplayUrl ? (
                    <Tooltip
                        title={
                            referenceOnScreen
                                ? t("puzzle.jigsaw.hide-image", "Hide image")
                                : t("puzzle.jigsaw.show-image", "Show image")
                        }
                        arrow
                    >
                        <IconButton
                            size="small"
                            data-play-toolbar-no-drag
                            onClick={toggleReferenceOnScreen}
                            aria-pressed={referenceOnScreen}
                            disabled={puzzleSolved}
                            sx={{
                                flexShrink: 0,
                                color: referenceOnScreen ? "#143024" : "#3BA666",
                                bgcolor: referenceOnScreen ? "#d7f0e2" : "transparent",
                                ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                            }}
                            aria-label={
                                referenceOnScreen
                                    ? t("puzzle.jigsaw.hide-image", "Hide image")
                                    : t("puzzle.jigsaw.show-image", "Show image")
                            }
                        >
                            <ImageOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <Tooltip
                    title={
                        showTileNumbers
                            ? t("puzzle.sliding.show-image-cells", "Show picture")
                            : t("puzzle.sliding.show-number-cells", "Show numbers")
                    }
                    arrow
                >
                    <IconButton
                        ref={setTileNumberBtnEl}
                        size="small"
                        data-play-toolbar-no-drag
                        onClick={() => {
                            setShowTileNumbers((on) => !on);
                            if (tileNumberHintOpen) dismissTileNumberHint();
                        }}
                        sx={{
                            flexShrink: 0,
                            color: showTileNumbers ? "#143024" : "#3BA666",
                            bgcolor: showTileNumbers ? "#d7f0e2" : "transparent",
                            ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                        }}
                        aria-label={
                            showTileNumbers
                                ? t("puzzle.sliding.show-image-cells", "Show picture")
                                : t("puzzle.sliding.show-number-cells", "Show numbers")
                        }
                        aria-pressed={showTileNumbers}
                    >
                        {showTileNumbers ? (
                            <ImageOutlinedIcon fontSize="small" />
                        ) : (
                            <NumbersIcon fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
                <Popover
                    open={hideSelectors && tileNumberHintOpen && Boolean(tileNumberBtnEl)}
                    anchorEl={tileNumberBtnEl}
                    onClose={dismissTileNumberHint}
                    hideBackdrop
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    disableScrollLock
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{ pointerEvents: "none" }}
                    slotProps={{
                        paper: {
                            sx: { mt: 0.75, px: 1.5, py: 1, maxWidth: 220, pointerEvents: "auto" },
                        },
                    }}
                >
                    <Typography variant="body2">
                        {t(
                            "puzzle.sliding.number-cells-hint",
                            "Show 1, 2, 3… on each tile, counting from the top-left.",
                        )}
                    </Typography>
                </Popover>
                <Tooltip title={t("nav.settings", "Settings")} arrow>
                    <IconButton
                        size="small"
                        data-play-toolbar-no-drag
                        onClick={(e) => setSettingsAnchor(e.currentTarget)}
                        sx={{ flexShrink: 0, color: "#3BA666", ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX }}
                        aria-label={t("nav.settings", "Settings")}
                        aria-haspopup="true"
                        aria-expanded={Boolean(settingsAnchor)}
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </PlayToolbarScrollRow>
            {ghostHintMessage ? (
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ px: 0.5, mt: -0.5, fontWeight: 600 }}
                >
                    {ghostHintMessage}
                </Typography>
            ) : null}
            <LoginConfirmDialog
                open={ghostLoginDialogOpen}
                onClose={() => setGhostLoginDialogOpen(false)}
                onConfirm={() => {
                    navigate("/login", {
                        state: { from: { pathname: window.location.pathname } },
                    });
                }}
                title={t("puzzle.sliding.ghost-hint-login-title", "Login required")}
                message={t(
                    "puzzle.sliding.ghost-hint-need-login",
                    "Please sign in to use hints.",
                )}
            />
            {/* Welcome-back popup disabled — timer still pauses while tab is hidden
            <GamePausedOverlay
                open={hideSelectors && pauseOverlayOpen}
                onContinue={() => {
                    continuePlaying();
                    setTimerPaused((prev) => {
                        if (
                            prev
                            && timerPauseStartedAtRef.current != null
                            && playSessionStartMsRef.current != null
                        ) {
                            playSessionStartMsRef.current +=
                                Date.now() - timerPauseStartedAtRef.current;
                            timerPauseStartedAtRef.current = null;
                        }
                        return false;
                    });
                }}
            />
            */}
            {/* Always mounted so BGM keeps playing when settings closes */}
            <Box
                ref={bgmButtonWrapRef}
                aria-hidden
                sx={{
                    position: "fixed",
                    left: -9999,
                    top: 0,
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                    opacity: 0,
                    pointerEvents: "none",
                }}
            >
                <AudioButton
                    key={`bgm-${bgmAutoPlay}`}
                    active={hideSelectors}
                    tracks={PUZZLE_BGM_TRACKS}
                    defaultPlaying={bgmAutoPlay}
                    cloudUserId={cloudUserId}
                    leadingDivider={false}
                    onPlayingChange={setBgmPlaying}
                />
            </Box>
            <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                disableScrollLock
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            p: 1.5,
                            minWidth: 200,
                            borderRadius: 2,
                            boxShadow: 4,
                        },
                    },
                }}
            >
                {isAuthenticated ? (
                    <>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                        >
                            {t("puzzle.sliding.settings-wallet", "Balance")}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 0.75,
                                mb: 1.25,
                            }}
                        >
                            {[
                                {
                                    key: "play",
                                    label: t("puzzle.sliding.settings-play-credits", "Play"),
                                    value: walletPlayCredits,
                                    icon: <TollIcon sx={{ fontSize: "1.1rem", color: "#2761F5" }} />,
                                },
                                {
                                    key: "hints",
                                    label: t("puzzle.sliding.settings-hint-credits", "Hints"),
                                    value: walletHintCredits,
                                    icon: (
                                        <LightbulbOutlinedIcon
                                            sx={{ fontSize: "1.1rem", color: "#3BA666" }}
                                        />
                                    ),
                                },
                                {
                                    key: "coins",
                                    label: t("puzzle.sliding.settings-coins", "Coins"),
                                    value: walletPuzzleCoins,
                                    icon: (
                                        <MonetizationOnIcon
                                            sx={{ fontSize: "1.1rem", color: "#d97706" }}
                                        />
                                    ),
                                },
                            ].map((item) => (
                                <Box
                                    key={item.key}
                                    sx={{
                                        textAlign: "center",
                                        py: 0.75,
                                        px: 0.5,
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "action.hover",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", fontWeight: 600, lineHeight: 1.2 }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.35,
                                            mt: 0.25,
                                        }}
                                    >
                                        {item.icon}
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 800, lineHeight: 1.2 }}
                                        >
                                            {walletBalancesLoading && item.value == null
                                                ? "…"
                                                : Number(item.value ?? 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Divider sx={{ my: 1.25 }} />
                    </>
                ) : null}
                {originalImageDialogUrl ? (
                    <>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
                        >
                            {t("puzzle.jigsaw.reference-image-menu")}
                        </Typography>
                        <MenuItem
                            onClick={handleOpenFullImage}
                            sx={{
                                borderRadius: 1,
                                mx: -0.5,
                                minHeight: 32,
                                py: 0.5,
                                fontSize: "0.8125rem",
                            }}
                        >
                            {t("puzzle.jigsaw.open-full-image")}
                        </MenuItem>
                        <MenuItem
                            onClick={handleToggleReferenceFromMenu}
                            sx={{
                                borderRadius: 1,
                                mx: -0.5,
                                minHeight: 32,
                                py: 0.5,
                                fontSize: "0.8125rem",
                            }}
                        >
                            {referenceOnScreen
                                ? t("puzzle.jigsaw.hide-on-screen")
                                : t("puzzle.jigsaw.show-on-screen")}
                        </MenuItem>
                        <Divider sx={{ my: 1.25 }} />
                    </>
                ) : null}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                        >
                            {t("puzzle.sliding.number-tile-theme", "Number tiles")}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={tileNumberTheme}
                            onChange={handleTileNumberThemeChange}
                            sx={ACCOUNT_SETTINGS_TOGGLE_GROUP_SX}
                            aria-label={t("puzzle.sliding.number-tile-theme", "Number tiles")}
                        >
                            <ToggleButton value="light" sx={{ px: 1.25, typography: "caption" }}>
                                {t("puzzle.sliding.number-tile-theme-light", "Light")}
                            </ToggleButton>
                            <ToggleButton value="dark" sx={{ px: 1.25, typography: "caption" }}>
                                {t("puzzle.sliding.number-tile-theme-dark", "Dark")}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                        >
                            {t("puzzle.jigsaw.background-music", "Background music")}
                        </Typography>
                        <Tooltip
                            title={bgmPlaying ? t("audio-button.mute") : t("audio-button.play")}
                            arrow
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const btn = bgmButtonWrapRef.current?.querySelector("button");
                                        btn?.click();
                                    }}
                                    sx={{
                                        color: "#3BA666",
                                        ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                    }}
                                    aria-label={
                                        bgmPlaying ? t("audio-button.mute") : t("audio-button.play")
                                    }
                                >
                                    {bgmPlaying ? (
                                        <MusicNoteIcon fontSize="small" />
                                    ) : (
                                        <MusicOffIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                        >
                            {t("puzzle.jigsaw.sound-effects", "Sound effects")}
                        </Typography>
                        <Tooltip
                            title={
                                soundEffectsEnabled
                                    ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                    : t("puzzle.jigsaw.enable-sound-effects", "Enable sound effects")
                            }
                            arrow
                        >
                            <IconButton
                                size="small"
                                onClick={handleToggleSoundEffects}
                                sx={{
                                    color: "#3BA666",
                                    ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX,
                                }}
                                aria-label={
                                    soundEffectsEnabled
                                        ? t("puzzle.jigsaw.mute-sound-effects", "Mute sound effects")
                                        : t(
                                              "puzzle.jigsaw.enable-sound-effects",
                                              "Enable sound effects",
                                          )
                                }
                                aria-pressed={!soundEffectsEnabled}
                            >
                                {soundEffectsEnabled ? (
                                    <VolumeUpIcon fontSize="small" />
                                ) : (
                                    <VolumeOffIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Popover>
            </>
            ) : (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                {toolbarLeading ? (
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{toolbarLeading}</Box>
                ) : null}
                <Box sx={{ flexShrink: 0 }}>
                    <Tooltip title={t("puzzles-browse.sliding-play-grid-size")} arrow>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={sizesAvailable.includes(gridSize) ? gridSize : null}
                            onChange={(e, next) => next != null && setGridSize(next)}
                            sx={ACCOUNT_SETTINGS_TOGGLE_GROUP_SX}
                            aria-label={t("puzzles-browse.sliding-play-grid-size")}
                        >
                            {SIZE_OPTIONS.map((s) => (
                                <ToggleButton
                                    key={s}
                                    value={s}
                                    disabled={!sizesAvailable.includes(s)}
                                    sx={{ px: 1.5 }}
                                >
                                    {t("puzzles-browse.sliding-play-grid-option", { s })}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Tooltip>
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                    <Tooltip title={t("puzzles-browse.sliding-play-level")} arrow>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={DIFF_LEVELS.includes(level) ? level : null}
                            onChange={(e, next) => next != null && setLevel(next)}
                            sx={ACCOUNT_SETTINGS_TOGGLE_GROUP_SX}
                            aria-label={t("puzzles-browse.sliding-play-level")}
                        >
                            {DIFF_LEVELS.map((lv) => (
                                <ToggleButton key={lv} value={lv} sx={{ px: 1.25, typography: "caption" }}>
                                    {t(LEVEL_LABEL_KEYS[lv])}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Tooltip>
                </Box>
                <PlayLayoutSplitSelect
                    value={splitKey}
                    onChange={setSplitKey}
                    ariaLabel={t("puzzles-browse.sliding-play-layout-aria")}
                />
            </Box>
            )}

            {!canRenderPuzzle ? (
                <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.86)" }}>
                    {t("puzzles-browse.sliding-no-variant")}
                </Typography>
            ) : puzzleSolved && referenceDisplayUrl ? (
                <Box sx={boardSplitOuterSx}>
                    <Box
                        sx={{
                            flex: "1 1 0",
                            minHeight: 0,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                        }}
                    >
                        {renderSolvedOriginalSquare()}
                    </Box>
                </Box>
            ) : useBoardAdaptiveSplit ? (
                <Box sx={boardSplitOuterSx}>
                    <Box
                        sx={{
                            flex: "1 1 0",
                            minHeight: 0,
                            minWidth: 0,
                            width: "100%",
                            position: "relative",
                            containerType: "size",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {boardPuzzleSquare}
                        </Box>
                        {boardReferencePreview ? (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: 8,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    zIndex: 3,
                                    /* Fit the square preview into the left gutter beside the centered board. */
                                    width:
                                        "max(0px, calc((100cqi - min(100cqi, 100cqb)) / 2 - 16px))",
                                    aspectRatio: "1 / 1",
                                    maxHeight: "min(100cqb, 100cqi)",
                                    pointerEvents: "auto",
                                    /* Hide when there is no real left gutter (portrait / narrow). */
                                    "@container (max-aspect-ratio: 1/1)": {
                                        display: "none",
                                    },
                                    "@supports not (width: 1cqi)": {
                                        width: "22%",
                                        maxWidth: 220,
                                    },
                                }}
                            >
                                {boardReferencePreview}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            ) : boardPuzzleOnlyLayout ? (
                <Box sx={boardSplitOuterSx}>
                    <Box
                        sx={{
                            flex: "1 1 0",
                            minHeight: 0,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                        }}
                    >
                        {boardPuzzleSquare}
                    </Box>
                </Box>
            ) : (
                <Stack spacing={2} sx={{ width: "100%" }}>
                    {/* Row 1: column titles (story picker route only; board route uses toolbar icons). */}
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            width: "100%",
                            m: 0,
                            alignItems: "center",
                            ...(hideSelectors ? { display: { xs: "none", sm: "flex" } } : {}),
                        }}
                    >
                        {splitKey !== "hide" && (
                        <Grid size={{ xs: 12, sm: splitSpans.left }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    width: "100%",
                                    minWidth: 0,
                                }}
                            >
                                <Typography variant="subtitle2" component="p" sx={{ m: 0, flex: "1 1 auto", minWidth: 0 }}>
                                    {t("puzzles-browse.sliding-play-original")}
                                </Typography>
                                {storyId && originalImageDialogUrl ? (
                                    <IconImagePreview
                                        imageUrl={originalImageDialogUrl}
                                        hideTitle
                                    />
                                ) : null}
                            </Box>
                        </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: splitSpans.right }}>
                            {splitKey !== "12-12" && splitKey !== "hide" && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        width: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        component="p"
                                        sx={{ m: 0, flex: "1 1 auto", minWidth: 0 }}
                                    >
                                        {t("puzzles-browse.sliding-play-puzzle-heading")}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>

                    {/* Row 2: reference + play boards only */}
                    <Grid container spacing={2} sx={{ width: "100%", m: 0, alignItems: "stretch" }}>
                        {splitKey !== "hide" && referenceDisplayUrl ? (
                        <Grid size={{ xs: 12, sm: splitSpans.left }} sx={{ display: "flex" }}>
                            <Box sx={{ width: "100%", alignSelf: "stretch", minWidth: 0 }}>
                                <StoryReferenceWithGrid
                                    imageUrl={referenceDisplayUrl}
                                    gridSize={resolvedGs}
                                    alt={t("puzzles-browse.sliding-play-reference-alt")}
                                    squareFrame
                                />
                            </Box>
                        </Grid>
                        ) : null}
                        <Grid size={{ xs: 12, sm: splitSpans.right }} sx={{ display: "flex" }}>
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-start",
                                    alignItems: "stretch",
                                    alignSelf: "stretch",
                                }}
                            >
                                {splitKey === "12-12" && (
                                    <Typography variant="subtitle2" component="p" sx={{ m: 0, mb: 1 }}>
                                        {t("puzzles-browse.sliding-play-puzzle-heading")}
                                    </Typography>
                                )}
                                <Box sx={{ width: "100%", maxWidth: "100%" }}>
                                    {puzzleBoardBox(false)}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Stack>
            )}
            {successCardNode}

            {hideSelectors && originalImageDialogUrl ? (
                <Dialog
                    open={imageDialogOpen}
                    onClose={() => setImageDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogContent sx={{ p: 1.5, position: "relative" }}>
                        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                            <IconButton
                                onClick={() => setImageDialogOpen(false)}
                                sx={{
                                    bgcolor: "background.paper",
                                    boxShadow: 1,
                                    "&:hover": { bgcolor: "background.paper" },
                                }}
                                aria-label={t("common.close", "Close")}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                pt: 4,
                            }}
                        >
                            <Box
                                component="img"
                                src={originalImageDialogUrl}
                                alt={t("puzzles-browse.sliding-play-reference-alt")}
                                sx={{
                                    width: "100%",
                                    maxHeight: "85vh",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            ) : null}
            </Box>
        </Stack>
    );
}

SlidingPuzzlePlay.propTypes = {
    templates: PropTypes.arrayOf(PropTypes.object),
    imageUrl: PropTypes.string,
    previewImageUrl: PropTypes.string,
    toolbarLeading: PropTypes.node,
    /** Logged-in cloud user id (`userInfo.cloudUser.id`) — enables `/home/sliding-play` sync. */
    cloudUserId: PropTypes.string,
    /** Signed-in member (any plan) — drives success-card CTA vs guest Sign Up. */
    isAuthenticated: PropTypes.bool,
    /** Story UUID — enables link to `/read-story/:storyId`. */
    storyId: PropTypes.string,
    /** Title for social share on board route (`hideSelectors`). */
    sharePuzzleTitle: PropTypes.string,
    hideSelectors: PropTypes.bool,
    soundEnabled: PropTypes.bool,
    bgmAutoPlay: PropTypes.bool,
};
